import { HttpErrorResponse } from '@angular/common/http';
import { OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AddressFormData } from 'src/app/core/dtos/address-form-data';
import { ChildAccountOrderAddressRequest, CountryDto, PersonalLookupInfoForTestRegistrationDto, SaveChildUserAccountIdentityInfoRequest, UserAddressDto, UserOrderAddressDto } from 'src/app/core/dtos/dto';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { AccountService } from 'src/app/core/services/account.service';
import { CountryRegionService } from 'src/app/core/services/country-region.service';
import { selectChildTermName } from 'src/app/state/my-account.selectors';

@Component({
  selector: 'app-edit-child-personal-info',
  templateUrl: './edit-child-personal-info.component.html',
  styleUrls: ['./edit-child-personal-info.component.scss']
})
export class EditChildPersonalInfoComponent implements OnInit {
  selectChildTermName$: Observable<string>;
  childUserAccountEmail: string;
  childAccountAddress: UserOrderAddressDto;
  countries: CountryDto[];
  billingAddressFormData: AddressFormData;
  shippingAddressFormData: AddressFormData;
  submitted: boolean;
  userAccountId: number;
  personalLookupInfo: PersonalLookupInfoForTestRegistrationDto;

  constructor(
    private accountService: AccountService,
    private activatedRoute: ActivatedRoute,
    private countryService: CountryRegionService,
    private store: Store,
    private toastService: HotToastService,
  ) { }

  ngOnInit(): void {
    this.selectChildTermName$ = this.store.pipe(select(selectChildTermName));
    this.activatedRoute.queryParams
      .subscribe(params => {
        this.userAccountId = params.userAccountId;
        this.childUserAccountEmail = params.userAccountEmail;
      });
    this.loadUserAccountInfo();
    this.getCountries();
    this.getPersonalLookupInfo();
  }

  getPersonalLookupInfo() {
    this.accountService.getPersonalLookupInfo().subscribe(
      (result) => {
        this.personalLookupInfo = result.content;
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  getCountries() {
    this.countryService.getCountries().subscribe(
      result => {
        this.countries = result.content;
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  billingFormValueChanged(formData: AddressFormData) {
    this.billingAddressFormData = formData;
  }

  shippingFormValueChanged(formData: AddressFormData) {
    this.shippingAddressFormData = formData;
  }


  loadUserAccountInfo() {
    this.accountService.getChildAccountAddress(this.userAccountId).subscribe(
      (result) => {
        this.childAccountAddress = result.content;
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.toastService.warning("Could not load child account information.");
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  onSubmit() {
    this.submitted = true;

    let address: ChildAccountOrderAddressRequest = null;
    let canSave = false;

    if (this.childAccountAddress.areShippingBillingAddressSame && this.shippingAddressFormData?.isValid) {
      address = this.setAddresses(this.shippingAddressFormData.address, this.shippingAddressFormData.address);
      canSave = true;
    } else if (this.billingAddressFormData?.isValid && this.shippingAddressFormData?.isValid) {
      address = this.setAddresses(this.billingAddressFormData.address, this.shippingAddressFormData.address);
      canSave = true;
    }

    if (canSave) {
      this.accountService.updateChildAccountAddress(address)
        .subscribe(
          () => {
            this.toastService.success("Successfully updated client account shipping and billing information.");
          },
          (error: HttpErrorResponse) => {
            if (error.status !== 401) {
              const errorMessage = error.error as HttpErrorResponseMessages;
              if (errorMessage?.reasons) {
                errorMessage.reasons.forEach(reasons => {
                  this.toastService.warning(reasons.errorMessage);
                });
              }
              else {
                this.toastService.warning("Unable to save personal information because of an unknown problem. Please contact Paragon.");
              }
            }
          });
    } else {
      this.toastService.warning("Please fill in the missing fields");
    }
  }

  setAddresses(billingAddress: UserAddressDto, shippingAddress: UserAddressDto): ChildAccountOrderAddressRequest {
    const newAddress: ChildAccountOrderAddressRequest = {
      billingAddress: { ...billingAddress, fullName: this.childAccountAddress.shippingAddress.fullName },
      shippingAddress: { ...shippingAddress, fullName: this.childAccountAddress.shippingAddress.fullName },
      areShippingBillingAddressSame: this.childAccountAddress.areShippingBillingAddressSame,
      needShippingAddress: this.childAccountAddress.needShippingAddress,
      childUserAccountId: this.userAccountId
    };
    return newAddress;
  }

  onProofOfIdentityFormSubmit(proofOfIdentityPageSaveInfo: SaveChildUserAccountIdentityInfoRequest) {
    this.accountService.updateChildAccountIdentityInfo(proofOfIdentityPageSaveInfo).subscribe(
      () => {
        this.toastService.success("Successfully saved personal information.");
      },
      (error: HttpErrorResponse) => {
        if (error.status !== 401) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          if (errorMessage?.reasons) {
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
          else {
            this.toastService.warning("Unable to save personal information because of an unknown problem. Please contact Paragon.");
          }
        }
      });
  }
}
